import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '@hooks';
import Drawer from '@components/Drawer';
import Card from '@components/Card';
import { Typography } from '@components/Typography';
import { AccIntegrationName } from '@constants/integrations';
import { ROLE } from '@constants/user';
import { handleApiError } from '@helpers/api';
import { alertActions, ifoodActions, accountActions, locationsProfilesActions } from '@actions';
import { ifoodIntegrationService } from '@services';
import { RoutePath } from '@constants/routes';

const IFoodIntegration = ({ onClose, onConnect, isOpen, selectedCompany, setSelectedCompany }) => {
    const { t } = useTranslation();
    const [merchant, setMerchant] = useState({});

    const account = useSelector(state => state.account.account);
    const user = useSelector(state => state.auth.user);
    const isAdmin = user.role === ROLE.ADMIN;
    const isSuperAdmin = user?.role === ROLE.SUPER_ADMIN;

    const MERCHANTS = useSelector(state => state.ifood.data);
    const history = useHistory();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(ifoodActions.get());
    }, [dispatch]);

    useEffect(() => {
        if (MERCHANTS.length) {
            const availableMerchant = MERCHANTS.find(m => !m.connected);
            if (availableMerchant) {
                setMerchant(availableMerchant);
            }
        }
    }, [MERCHANTS]);

    const IFOOD_CONNECTED = account?.integrations.find(i => i.type === AccIntegrationName.iFood)?.isActive;

    const goToIntegrations = () => history.push(RoutePath.AccountSettingsIntegrations);

    const onConnectIFood = async () => {
        try {
            onClose();
            const updatedCompany = await ifoodIntegrationService.editProfile(selectedCompany?.companyId, {
                locationId: selectedCompany?.companyId,
                restaurantId: merchant.id || null,
                name: merchant.name || null,
            });
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            dispatch(ifoodActions.reset());
            setSelectedCompany(updatedCompany);
            dispatch(alertActions.success(t('SettingsCompanies.connectIntegrationMsg')));
        } catch (error) {
            handleApiError(error);
        } finally {
            onConnect();
        }
    };

    const getPrimaryActionProps = () => {
        if (!IFOOD_CONNECTED && isSuperAdmin) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: false,
            };
        } else if (IFOOD_CONNECTED && (isSuperAdmin || isAdmin)) {
            return {
                primaryAction: onConnectIFood,
                primaryText: t('buttons.connect'),
                primaryDisabled: !merchant.id,
            };
        } else if (!IFOOD_CONNECTED && !isSuperAdmin) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: true,
            };
        }
    };

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            title={
                IFOOD_CONNECTED
                    ? t('SettingsCompanies.connectLocation')
                    : t('SettingsCompanies.connectDrawerNotConnected')
            }
            subtitle={
                IFOOD_CONNECTED
                    ? t('SettingsCompanies.connectDrawerDescription')
                    : t('SettingsCompanies.connectDrawerNotConnectedDescription')
            }
            {...getPrimaryActionProps()}
            secondaryText={IFOOD_CONNECTED && (isSuperAdmin || isAdmin) ? t('buttons.cancel') : null}
        >
            <div style={{ display: 'grid', gridGap: '8px' }}>
                {IFOOD_CONNECTED &&
                    merchant &&
                    MERCHANTS.map(m => (
                        <Card
                            key={m.id}
                            type="radio"
                            selected={merchant.id === m.id && !m.connected}
                            title={m.name}
                            value={m.id}
                            setValue={() => setMerchant(m)}
                            disabled={m.connected}
                        >
                            {m.connected && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: '#416774',
                                        padding: '2px 6px',
                                        borderRadius: '18px',
                                        width: 'fit-content',
                                    }}
                                >
                                    {m.connectedLocationName}
                                </Typography>
                            )}
                        </Card>
                    ))}
            </div>
        </Drawer>
    );
};

export default IFoodIntegration;
